import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowRight, ArrowUpRight, ArrowLeft, CheckCircle2,
  Building2, Workflow, Users, FileText, Megaphone, Wrench, MessageSquare,
  Plug, ShieldCheck, Sparkles, TrendingUp, Wallet, Home, BarChart3,
  ClipboardCheck, Store, Languages, AlertTriangle, History, CalendarDays, Mail,
} from 'lucide-react';
import TopNavMega from '@/components/landing/TopNavMega';
import { Section, tt } from '@/components/landing/atoms';
import { PRODUCTS_BY_SLUG, PRODUCT_CATALOG } from '@/components/landing/products';
import BookDemoModal from '@/components/BookDemoModal';

const ICON = {
  Building2, Workflow, Users, FileText, Megaphone, Wrench, MessageSquare,
  Plug, ShieldCheck, Sparkles, TrendingUp, Wallet, Home, BarChart3,
  ClipboardCheck, Store, Languages, AlertTriangle, History, CalendarDays,
  Mail, CheckCircle2,
};

// ─────────────────────────────────────────────────────────────────────────────
// One reusable product page used by all 10 product slugs.
// AppFolio-style: clean white hero · headline + sub · 5 benefit cards ·
// CTA · "explore more products" footer.
// ─────────────────────────────────────────────────────────────────────────────
export default function ProductPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'ar' ? 'ar' : 'en';
  const isAr = lang === 'ar';
  const [demoOpen, setDemoOpen] = React.useState(false);

  const product = PRODUCTS_BY_SLUG[slug];
  if (!product) {
    return (
      <div className="reva-landing min-h-screen flex items-center justify-center" dir={isAr ? 'rtl' : 'ltr'}>
        <div className="text-center">
          <h1 className="text-2xl font-semibold text-[#0E1320]">{isAr ? 'الصفحة غير موجودة' : 'Product not found'}</h1>
          <button onClick={() => navigate('/')} className="mt-4 reva-btn-outline">{isAr ? 'العودة للرئيسية' : 'Back to home'}</button>
        </div>
      </div>
    );
  }

  const setLang = (l) => i18n.changeLanguage(l);
  const Icon = product.icon;

  return (
    <div className="reva-landing min-h-screen" dir={isAr ? 'rtl' : 'ltr'} data-testid={`product-page-${slug}`}>
      <TopNavMega lang={lang} setLang={setLang} onDemo={() => setDemoOpen(true)} onTour={() => navigate('/')} />

      {/* HERO */}
      <Section className="pt-14 md:pt-24 pb-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1.5 text-[12px] text-[#4A5263] hover:text-[#0E1320] mb-6" data-testid="product-back">
          {isAr ? <ArrowRight className="h-3 w-3" /> : <ArrowLeft className="h-3 w-3" />}
          {isAr ? 'رجوع' : 'Back'}
        </button>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <span className="reva-eyebrow-light reva-rise" style={{ animationDelay: '0.04s' }}>
              <Icon className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
              {tt(product.eyebrow, lang)}
            </span>

            <h1 className="reva-display-light mt-5 max-w-[20ch] text-[36px] sm:text-[48px] lg:text-[56px] leading-[1.05] tracking-[-0.02em] reva-rise"
                style={{ animationDelay: '0.14s' }}
                data-testid={`product-${slug}-title`}>
              {tt(product.hero.title, lang)}
            </h1>

            <p className="mt-5 max-w-2xl text-[16px] leading-[1.6] text-[#2A3340] reva-rise"
               style={{ animationDelay: '0.24s' }}
               data-testid={`product-${slug}-sub`}>
              {tt(product.hero.sub, lang)}
            </p>

            <div className="mt-7 flex flex-wrap items-center gap-3 reva-rise" style={{ animationDelay: '0.34s' }}>
              <button onClick={() => setDemoOpen(true)} className="reva-btn-primary" data-testid={`product-${slug}-cta-demo`}>
                {isAr ? 'احجز عرضًا' : 'Book a Demo'} <ArrowRight className="h-4 w-4" />
              </button>
              <span className="inline-flex items-center gap-1.5 text-[11.5px] text-[#4A5263]">
                <CheckCircle2 className="h-3.5 w-3.5 text-[color:var(--reva-signal)]" />
                {tt(product.outcomeChip, lang)}
              </span>
            </div>
          </div>

          {/* Decorative product card */}
          <div className="lg:col-span-5 reva-rise" style={{ animationDelay: '0.32s' }}>
            <div className="rounded-2xl border border-[#E1E6EC] bg-gradient-to-br from-white to-[#F7FBF9] p-10 flex items-center justify-center min-h-[280px] shadow-sm" data-testid={`product-${slug}-art`}>
              <div className="text-center">
                <div className="inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-[rgba(0,168,107,0.08)] border border-[rgba(0,168,107,0.18)]">
                  <Icon className="h-9 w-9 text-[#00734A]" strokeWidth={1.4} />
                </div>
                <div className="mt-5 text-[13px] reva-mono uppercase tracking-[0.18em] text-[#4A5263]">
                  REVA · {tt(product.short, lang)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* BENEFITS */}
      <Section className="py-16 md:py-20">
        <div className="grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E]">
              {isAr ? 'ما الذي تحصل عليه' : 'What you get'}
            </div>
            <h2 className="mt-4 reva-display-light text-3xl sm:text-4xl tracking-[-0.02em] leading-[1.10] max-w-[24ch]">
              {isAr ? 'نتائج تجاريّة' : 'Business outcomes,'}{' '}
              <span className="reva-signal-text-light">{isAr ? 'وليس مجرّد ميّزات.' : 'not just features.'}</span>
            </h2>
          </div>
        </div>

        <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`product-${slug}-benefits`}>
          {(product.benefits || []).map((b, i) => {
            const BIcon = ICON[b.icon] || Sparkles;
            return (
              <div key={i} className="reva-module-card" data-testid={`product-${slug}-benefit-${i}`}>
                <div className="reva-module-card__icon">
                  <BIcon className="h-5 w-5 text-[#00734A]" strokeWidth={1.7} />
                </div>
                <h3 className="mt-5 text-[16px] font-semibold text-[#0E1320] tracking-tight">{tt(b.t, lang)}</h3>
                <p className="mt-2 text-[13px] leading-[1.55] text-[#4A5263]">{tt(b.d, lang)}</p>
              </div>
            );
          })}
        </div>
      </Section>

      {/* CTA */}
      <Section className="py-16">
        <div className="rounded-3xl bg-gradient-to-br from-[#0B1220] via-[#131A2A] to-[#0E1320] p-10 md:p-14 flex flex-col md:flex-row items-center justify-between gap-6 text-white">
          <div>
            <div className="text-[10.5px] reva-mono uppercase tracking-[0.20em] text-white/55">
              {isAr ? 'الخطوة التّالية' : 'Next step'}
            </div>
            <h3 className="mt-3 text-2xl sm:text-3xl font-semibold tracking-[-0.02em] leading-[1.15] max-w-[24ch]">
              {isAr ? 'شاهد كيف تعمل REVA على محفظتك خلال 30 دقيقة.' : 'See REVA running on your portfolio in 30 minutes.'}
            </h3>
          </div>
          <button onClick={() => setDemoOpen(true)} className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0E1320] text-[13.5px] font-semibold hover:bg-[#1FD18A] hover:text-[#0B1220] transition-colors" data-testid={`product-${slug}-cta-bottom`}>
            {isAr ? 'احجز عرضًا' : 'Book a Demo'}
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </Section>

      {/* Related products */}
      <Section className="pb-24">
        <div className="text-[10.5px] reva-mono uppercase tracking-[0.18em] text-[#71777E] mb-5">
          {isAr ? 'منتجات ذات صلة' : 'Related products'}
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {PRODUCT_CATALOG.filter(p => p.slug !== slug).slice(0, 4).map(p => {
            const PIcon = p.icon;
            return (
              <button key={p.slug} onClick={() => navigate(`/products/${p.slug}`)} className="reva-module-card text-start" data-testid={`product-${slug}-related-${p.slug}`}>
                <div className="flex items-start justify-between">
                  <div className="reva-module-card__icon">
                    <PIcon className="h-4.5 w-4.5 text-[#00734A]" strokeWidth={1.7} />
                  </div>
                  <ArrowUpRight className="h-4 w-4 text-[#71777E]" />
                </div>
                <div className="mt-4 text-[14px] font-semibold text-[#0E1320]">{tt(p.short, lang)}</div>
                <div className="mt-1.5 text-[12px] text-[#4A5263] leading-snug">{tt(p.blurb, lang)}</div>
              </button>
            );
          })}
        </div>
      </Section>
      <BookDemoModal open={demoOpen} onClose={() => setDemoOpen(false)} lang={lang} sourcePage={`/products/${slug}`} />
    </div>
  );
}
